import { inject, DestroyRef, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { catchError, tap, throwError, from, EMPTY } from 'rxjs';
import { distinctUntilChanged, switchMap, filter, delay, map } from 'rxjs/operators';
import { storeWithDevtools } from '../store-devtools';
import { IUserNotification } from './user-notification.model';
import { TaskDataService } from '../../services/task-data.service';
import { AuthService } from '../../services';
import { SignalRService } from '../../services/signal-r.service';
import { hasNotificationChanged, mergeNotification, normalizeTaskMessage } from './notification.helpers';
import { sendNotificationMessage } from '../../../types/resource';
import { MessageService } from '../../components/framework/services/message.service';

type NotificationStatus = 'initialising' | 'idle' | 'loading' | 'error';

interface NotificationState {
  notifications: IUserNotification[];
  status: NotificationStatus;
  error: string | null;
}

export const NotificationStore = signalStore(
  { providedIn: 'root' },
  withState<NotificationState>({
    notifications: [],
    status: 'initialising',
    error: null,
  }),
  storeWithDevtools('NotificationStore'),
  withComputed((store) => ({
    unreadCount: computed(() => store.notifications().filter(n => !n.isRead).length),
  })),
  withMethods((store, taskDataService = inject(TaskDataService), messageService = inject(MessageService)) => ({
    loadNotifications: () => {
      patchState(store, { status: 'loading', error: null });

      return taskDataService.getNotifications().pipe(
        map((notifications) => notifications.map(n => ({
          ...n,
          body: normalizeTaskMessage(n.body),
        }))),
        tap((notifications) => patchState(store, { notifications, status: 'idle' })),
        catchError((error: { message?: string }) => {
          patchState(store, { status: 'error', error: error?.message ?? 'Failed to load notifications.' });
          return throwError(() => error);
        }),
      );
    },
    addNotification: (notification: IUserNotification) => {
      const currentNotifications = store.notifications();
      const existing = currentNotifications.find(n => n.id === notification.id);

      notification.body = normalizeTaskMessage(notification.body);

      // Only update if it's a new notification or if it has changed
      if (!existing || hasNotificationChanged(existing, notification)) {
        const updatedNotifications = mergeNotification(currentNotifications, notification);

        // Call message service to show a toast for the new notification
        sendNotificationMessage(`New notification from ${notification.sender}`, messageService, true);
        patchState(store, { notifications: updatedNotifications });
      }
    },
    markAsRead: (ids: string[]) => {
      return taskDataService.setNotificationAsRead(ids).pipe(
        tap(() => {
          const updatedNotifications = store.notifications().map(notification =>
            ids.includes(notification.id)
              ? { ...notification, isRead: true }
              : notification
          );
          patchState(store, { notifications: updatedNotifications });
        }));
    }
  })),
  withHooks((
    store,
    authService = inject(AuthService),
    signalRService = inject(SignalRService),
    destroyRef = inject(DestroyRef)) => ({
      onInit() {
        // Subscribe to login state changes - load notifications when user logs in
        // Add delay to ensure token is available before making API call
        authService.loginChanged.pipe(
          distinctUntilChanged(),
          filter(isLoggedIn => isLoggedIn),
          delay(500), // Wait for token to be available
          switchMap(() => store.loadNotifications()),
          catchError((error) => {
            console.warn('⚠️ NotificationStore: Failed to load notifications on login:', error);
            return EMPTY;
          }),
          takeUntilDestroyed(destroyRef)
        ).subscribe();

        // Handle initial authentication state (page reload) - auto-load if already logged in
        // Longer delay on reload: token is already in storage so no extra wait is needed,
        // but pushing the fetch to 2 s ensures the grid renders its first page before
        // notifications compete for the main thread.
        from(authService.isAuthenticatedAsync()).pipe(
          filter(isAuth => isAuth),
          delay(2000),
          switchMap(() => store.loadNotifications()),
          catchError((error) => {
            console.warn('⚠️ NotificationStore: Failed to load notifications on init:', error);
            return EMPTY;
          }),
          takeUntilDestroyed(destroyRef)
        ).subscribe();

        signalRService.notificationReceived$.pipe(
          tap((notification) => {
            store.addNotification(notification);
          }),
          takeUntilDestroyed(destroyRef)
        ).subscribe();
      }
    })),
);